<template>
    <div class="email-content-builder">
        <div class="field_label">Contenu de l'email <span class="text-red">*</span></div>

        <ContentBuilderToolbar :blocks="blocks" :history="history" @undo="undo" @redo="redo" @clear-all="clearAll" />
        <v-row>
            <v-col cols="12" md="4">
                <div class="blocks-palette spo-card rounded bordered p-2">
                    <div class="font-weight-medium mb-2">Blocs disponibles</div>
                    <div class="mb-2 text-grey-darken-3">Glissez et déposez les blocs dans la zone de
                        construction pour
                        commencer
                        à
                        construire votre email</div>
                    <div class="block-item bordered" v-for="blockType in availableBlocks" :key="blockType.type"
                        draggable="true" @dragstart="onDragStart($event, blockType)">
                        <v-icon>{{ blockType.icon }}</v-icon>
                        <span>{{ blockType.label }}</span>
                    </div>
                </div>
            </v-col>
            <v-col cols="12" md="4">
                <div class="builder-canvas" :class="{ 'drag-over': isDragging }" @drop="onDrop" @dragover.prevent
                    @dragenter.prevent @dragleave="onDragLeave">

                    <div v-if="blocks.length === 0" class="empty-state">
                        <v-icon size="48" color="grey">mdi-email-outline</v-icon>
                        <p>Glissez des blocs ici pour commencer à construire votre email</p>
                    </div>

                    <div v-for="(block, index) in blocks" :key="block.id" :data-block-id="block.id"
                        class="block-wrapper" :class="{ 'selected': selectedBlockId === block.id }">

                        <!-- Handle de drag -->
                        <div class="drag-handle " draggable="true" @dragstart="onDragStart($event, block, index)">
                            <v-icon>mdi-drag</v-icon>
                        </div>


                        <!-- Contenu du bloc -->
                        <div class="block-content" v-if="isBlockEditing(block.id)">
                            <TextBlock v-if="block.type === 'text'" :block="block"
                                @update="updateBlock(index, $event)" />
                            <ImageBlock v-if="block.type === 'image'" :block="block"
                                @update="updateBlock(index, $event)" />
                            <ButtonBlock v-if="block.type === 'button'" :block="block"
                                @update="updateBlock(index, $event)" />
                            <SeparatorBlock v-if="block.type === 'separator'" :block="block"
                                @update="updateBlock(index, $event)" />
                            <div class="d-flex justify-center my-2">
                                <v-btn @click="stopEditing()" color="success" variant="elevated" elevation="0"
                                    v-if="isBlockEditing(block.id)">
                                    <v-icon size="16">mdi-check</v-icon> Valider
                                </v-btn>
                            </div>
                        </div>



                        <!-- Aperçu du bloc (quand pas en édition) -->
                        <div class="block-preview" v-else>
                            <div class="preview-placeholder">
                                <v-icon size="24" color="grey">{{ getBlockIcon(block.type) }}</v-icon>
                                <span class="text-grey">{{ getBlockLabel(block.type) }}</span>
                                <div class="d-flex justify-space-between ga-2">
                                    <v-btn size="small" @click="startEditing(block.id)" color="darkblue"
                                        variant="elevated" class="no-uppercase" elevation="0"
                                        v-if="!isBlockEditing(block.id)">
                                        <v-icon size="16">mdi-pencil</v-icon>
                                    </v-btn>
                                    <v-btn size="small" @click="duplicateBlock(index)" color="primary-dark"
                                        variant="elevated" elevation="0" class="no-uppercase">
                                        <v-icon size="16">mdi-content-duplicate</v-icon>
                                    </v-btn>
                                    <v-btn size="small" @click="deleteBlock(index)" color="error" variant="elevated"
                                        elevation="0" class="no-uppercase">
                                        <v-icon size="16">mdi-delete</v-icon>
                                    </v-btn>
                                </div>

                            </div>
                        </div>


                    </div>
                </div>
            </v-col>
            <v-col cols="12" md="4">
                <!-- Panneau de propriétés -->


                <!-- Panneau d'aperçu -->
                <div class="preview-panel">
                    <EmailPreviewPanel :blocks="blocks" />
                </div>
            </v-col>
        </v-row>


    </div>
</template>

<script>
import TextBlock from './blocks/TextBlock.vue'
import ImageBlock from './blocks/ImageBlock.vue'
import ButtonBlock from './blocks/ButtonBlock.vue'
import SeparatorBlock from './blocks/SeparatorBlock.vue'

import EmailPreviewPanel from './EmailPreviewPanel.vue'
import ContentBuilderToolbar from './ContentBuilderToolbar.vue'
import { getAvailableBlockTypes, createBlock, getBlockConfig } from './blocksConfig.js'
import { useCampaignStore } from '@/stores/mailingSystem/campaignStore.js'
import { mapStores } from 'pinia'


export default {
    name: 'ContentBuilder',
    components: {
        TextBlock,
        ImageBlock,
        ButtonBlock,
        SeparatorBlock,

        EmailPreviewPanel,
        ContentBuilderToolbar
    },

    data() {
        return {
            selectedBlockId: null,
            editingBlockId: null,
            draggedBlock: null,
            draggedIndex: null,
            isDragging: false,
            availableBlocks: getAvailableBlockTypes(),
            history: {
                undo: [],
                redo: []
            }
        }
    },
    computed: {
        ...mapStores(useCampaignStore),
        blocks() {
            return this.campaignStore.blocks_content
        },
        selectedBlock() {
            return this.campaignStore.blocks_content.find(block => block.id === this.selectedBlockId)
        },
        isBlockEditing() {
            return (blockId) => this.editingBlockId === blockId
        }
    },

    methods: {
        startEditing(blockId) {
            this.editingBlockId = blockId
        },

        stopEditing() {
            this.editingBlockId = null
        },

        getBlockIcon(blockType) {
            const blockConfig = getBlockConfig(blockType)
            return blockConfig?.icon || 'mdi-help'
        },

        getBlockLabel(blockType) {
            const blockConfig = getBlockConfig(blockType)
            return blockConfig?.label || 'Bloc inconnu'
        },

        onDragStart(event, blockType, index = null) {
            this.draggedBlock = blockType
            this.draggedIndex = index
            this.isDragging = true
            event.dataTransfer.effectAllowed = 'move'
        },

        onDrop(event) {
            event.preventDefault()

            if (this.draggedIndex !== null) {
                // Réorganisation d'un bloc existant
                this.reorderBlocks(event)
            } else {
                // Ajout d'un nouveau bloc
                this.addBlock(this.draggedBlock)
            }

            this.draggedBlock = null
            this.draggedIndex = null
            this.isDragging = false
        },

        onDragLeave(event) {
            // Vérifier si on quitte vraiment la zone de drop
            if (!event.currentTarget.contains(event.relatedTarget)) {
                this.isDragging = false
            }
        },

        addBlock(blockType) {
            this.saveToHistory()
            const newBlock = createBlock(blockType.type)
            this.campaignStore.addBlock(newBlock)
            this.selectBlock(newBlock.id)
        },


        updateBlock(index, data) {
            this.campaignStore.updateBlock(index, data)
        },

        updateSelectedBlock(data) {
            const index = this.campaignStore.blocks_content.findIndex(block => block.id === this.selectedBlockId)
            if (index !== -1) {
                this.updateBlock(index, data)
            }
        },

        selectBlock(blockId) {
            this.selectedBlockId = blockId
        },

        duplicateBlock(index) {
            this.saveToHistory()
            this.campaignStore.duplicateBlock(index)
        },

        deleteBlock(index) {
            this.saveToHistory()
            this.campaignStore.deleteBlock(index)
            if (this.selectedBlockId === this.campaignStore.blocks_content[index]?.id) {
                this.selectedBlockId = null
            }
        },

        reorderBlocks(event) {
            const dropTarget = this.getDropTarget(event)
            if (dropTarget !== null && dropTarget !== this.draggedIndex) {
                this.saveToHistory()
                this.campaignStore.reorderBlocks(this.draggedIndex, dropTarget)
            }
        },

        getDropTarget(event) {
            const rect = event.currentTarget.getBoundingClientRect()
            const y = event.clientY - rect.top

            let targetIndex = null
            let minDistance = Infinity

            this.campaignStore.blocks_content.forEach((block, index) => {
                const blockElement = this.$el.querySelector(`[data-block-id="${block.id}"]`)
                if (blockElement) {
                    const blockRect = blockElement.getBoundingClientRect()
                    const blockCenter = blockRect.top + blockRect.height / 2
                    const distance = Math.abs(y - blockCenter)

                    if (distance < minDistance) {
                        minDistance = distance
                        targetIndex = y < blockCenter ? index : index + 1
                    }
                }
            })

            return targetIndex
        },

        getPropertiesComponent(type) {
            const blockConfig = getBlockConfig(type)
            return blockConfig ? blockConfig.properties : null
        },

        // Méthodes pour la barre d'outils
        saveToHistory() {
            this.history.undo.push(JSON.parse(JSON.stringify(this.campaignStore.blocks_content)))
            this.history.redo = [] // Vider l'historique de redo
        },

        undo() {
            if (this.history.undo.length > 0) {
                const previousState = this.history.undo.pop()
                this.history.redo.push(JSON.parse(JSON.stringify(this.campaignStore.blocks_content)))
                this.campaignStore.blocks_content = previousState
            }
        },

        redo() {
            if (this.history.redo.length > 0) {
                const nextState = this.history.redo.pop()
                this.history.undo.push(JSON.parse(JSON.stringify(this.campaignStore.blocks_content)))
                this.campaignStore.blocks_content = nextState
            }
        },

        clearAll() {
            this.campaignStore.clearBlocks()
            this.selectedBlockId = null
            this.saveToHistory()
        },

    }
}
</script>

<style lang="scss" scoped>
.builder-container {
    display: grid;
    grid-template-columns: 1fr 600px 600px;
    gap: 16px;
    min-height: 500px;
}

.blocks-palette {

    h4 {
        margin-bottom: 16px;
        font-size: 14px;
        font-weight: 500;
    }

    .block-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px;
        background: white;
        border-radius: 4px;
        margin-bottom: 8px;
        cursor: grab;
        transition: all 0.2s;

        &:hover {
            background: #e3f2fd;
        }

        &:active {
            cursor: grabbing;
        }
    }
}

.builder-canvas {
    background: white;
    border: 2px dashed #ddd;
    border-radius: 8px;
    padding: 16px;
    min-height: 400px;
    transition: all 0.2s ease;

    &.drag-over {
        border-color: #2196f3;
        background: #f3f9ff;
        transform: scale(1.02);
    }
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 300px;
    color: #666;

    p {
        margin-top: 16px;
        text-align: center;
    }
}

.block-wrapper {
    position: relative;
    margin-bottom: 16px;
    border: 1px solid transparent;
    border-radius: 4px;
    transition: all 0.2s;

    &:hover {
        border-color: #e0e0e0;
    }



    &.dragging {
        opacity: 0.5;
        transform: rotate(2deg);
    }
}

.drag-handle {
    position: absolute;
    left: -20px;
    top: 50%;
    transform: translateY(-50%);
    background: #f5f5f5;
    border-radius: 4px;
    padding: 4px;
    cursor: grab;
    opacity: 0;
    transition: opacity 0.2s;

    .block-wrapper:hover & {
        opacity: 1;
    }

    &:active {
        cursor: grabbing;
    }
}

.block-content {
    padding: 16px;
}

.block-preview {
    padding: 16px;
    min-height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8f9fa;
    border: 2px dashed #e0e0e0;
    border-radius: 4px;
    margin: 8px;
}

.preview-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: #666;
    font-size: 14px;
}

.block-actions {

    opacity: 0;
    transition: opacity 0.2s;

    .block-wrapper:hover & {
        opacity: 1;
    }
}

.properties-panel {
    background: #f5f5f5;
    padding: 16px;
    border-radius: 8px;

    h4 {
        margin-bottom: 16px;
        font-size: 14px;
        font-weight: 500;
    }
}

.properties-content {
    background: white;
    padding: 16px;
    border-radius: 4px;
}

.right-panel {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.preview-panel {
    flex: 1;
    min-height: 300px;
}
</style>